import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { BaseAttachedResearch, EntityResearchHooks } from '@/types/research-attachments';
import { useResearchAttachmentOperations } from './use-research-attachment-operations';

export const createResearchHooks = <T extends BaseAttachedResearch>(
  entityType: string
): EntityResearchHooks<T> => {
  
  const useAttachedResearch = (entityId?: string) => {
    const operations = useResearchAttachmentOperations<T>(entityType);
    
    return useQuery({
      queryKey: [...operations.entityConfig.queryKey, entityId],
      queryFn: () => operations.fetchAttachments(entityId || ''),
      enabled: !!entityId,
    });
  };

  const useAttachResearch = () => {
    const queryClient = useQueryClient();
    const operations = useResearchAttachmentOperations<T>(entityType);
    
    return useMutation({
      mutationFn: async ({ entityId, researchId }: { entityId: string; researchId: string }) => {
        return operations.attachResearch(entityId, researchId);
      },
      onSuccess: (data, variables) => {
        toast.success(`Research attached to ${operations.entityConfig.displayName.toLowerCase()} successfully`);
        queryClient.invalidateQueries({ 
          queryKey: [...operations.entityConfig.queryKey, variables.entityId] 
        });
        return data;
      },
      onError: (error) => {
        console.error(`Error attaching research to ${entityType}:`, error);
        toast.error(`Failed to attach research to ${operations.entityConfig.displayName.toLowerCase()}`);
      },
    });
  };

  const useDetachResearch = () => {
    const queryClient = useQueryClient();
    const operations = useResearchAttachmentOperations<T>(entityType);
    
    return useMutation({
      mutationFn: async ({ attachmentId, entityId }: { attachmentId: string; entityId: string }) => {
        await operations.detachResearch(attachmentId, entityId);
        return { attachmentId, entityId };
      },
      onSuccess: (data) => {
        toast.success(`Research detached from ${operations.entityConfig.displayName.toLowerCase()} successfully`);
        queryClient.invalidateQueries({ 
          queryKey: [...operations.entityConfig.queryKey, data.entityId] 
        });
      },
      onError: (error) => {
        console.error(`Error detaching research from ${entityType}:`, error);
        toast.error(`Failed to detach research from ${operations.entityConfig.displayName.toLowerCase()}`);
      },
    });
  };

  const useUpdateResearchSummary = () => {
    const queryClient = useQueryClient();
    const operations = useResearchAttachmentOperations<T>(entityType);
    
    return useMutation({
      mutationFn: async ({ 
        attachmentId, 
        summary, 
        entityId 
      }: { 
        attachmentId: string; 
        summary: string; 
        entityId: string;
      }) => {
        return operations.updateSummary(attachmentId, summary, entityId);
      },
      onSuccess: (data, variables) => {
        console.log(`[${entityType}UpdateSummary] ===== MUTATION SUCCESS CALLBACK =====`);
        queryClient.invalidateQueries({ 
          queryKey: [...operations.entityConfig.queryKey, variables.entityId] 
        });
      },
      onError: (error, variables) => {
        console.error(`[${entityType}UpdateSummary] ===== MUTATION ERROR CALLBACK =====`);
        console.error(`Error updating ${entityType} research summary:`, error);
        toast.error(`Failed to generate ${operations.entityConfig.displayName.toLowerCase()} research summary`);
      },
    });
  };

  return {
    useAttachedResearch,
    useAttachResearch,
    useDetachResearch,
    useUpdateResearchSummary,
  };
};

// Convenience function to get hooks for any entity type
export const useUniversalResearchHooks = <T extends BaseAttachedResearch>(entityType: string) => {
  return createResearchHooks<T>(entityType);
}; 